import "shaka-player/dist/controls.css";
import shaka from "shaka-player/dist/shaka-player.ui";

import React, { ForwardedRef, useCallback, useEffect, useImperativeHandle, useRef, useState } from "react";

import CloseIcon from "@mui/icons-material/Close";
import { Dialog, DialogContent, DialogTitle, IconButton } from "@mui/material";

import { useAlert } from "../../contexts/AlertContext";

interface ShakaPlayerProps {
  src: string;
  config?: shaka.extern.PlayerConfiguration;
  chromeless?: boolean;
  className?: string;
  onError: (error: Error) => void;
  autoPlay?: boolean;
  width?: number;
  height?: number;
}

export interface ShakaPlayerRef {
  player: shaka.Player | null;
  ui: shaka.ui.Overlay | null;
  videoElement: HTMLVideoElement | null;
}

const ShakaPlayer = React.forwardRef<ShakaPlayerRef, ShakaPlayerProps>(
  ({ src, config, chromeless = false, className, onError, ...rest }, ref: ForwardedRef<ShakaPlayerRef>) => {
    const uiContainerRef = useRef<HTMLDivElement>(null);
    const videoRef = useRef<HTMLVideoElement>(null);

    const [player, setPlayer] = useState<shaka.Player | null>(null);
    const [ui, setUi] = useState<shaka.ui.Overlay | null>(null);

    // Effect to handle component mount & unmount.
    // Not related to the src prop, this hook creates a shaka.Player instance.
    // This should always be the first effect to run.
    useEffect(() => {
      if (!videoRef.current) return;

      const playerInstance = new shaka.Player();
      playerInstance.attach(videoRef.current);
      setPlayer(playerInstance);

      let uiInstance: shaka.ui.Overlay | null = null;
      if (!chromeless && uiContainerRef.current) {
        uiInstance = new shaka.ui.Overlay(playerInstance, uiContainerRef.current, videoRef.current);
        setUi(uiInstance);
      }

      return () => {
        playerInstance.destroy();
        if (uiInstance) {
          uiInstance.destroy();
        }
      };
    }, [chromeless]);

    // Keep shaka.Player.configure in sync.
    useEffect(() => {
      if (player && config) {
        player.configure(config);
      }
    }, [player, config]);

    // Load the source URL when we have one.
    useEffect(() => {
      if (player && src) {
        player.load(src).catch(onError);
      }
    }, [player, src, onError]);

    // Define a handle for easily referencing Shaka's player & UI APIs.
    useImperativeHandle(
      ref,
      () => ({
        get player() {
          return player;
        },
        get ui() {
          return ui;
        },
        get videoElement() {
          return videoRef.current;
        },
      }),
      [player, ui],
    );

    return (
      <div ref={uiContainerRef} className={className}>
        <video
          ref={videoRef}
          style={{
            maxWidth: "100%",
            width: "100%",
          }}
          {...rest}
        />
      </div>
    );
  },
);

const VideoPlayerModal: React.FC<{ title: string; videoUrl: string; onClose: () => void }> = ({
  title,
  videoUrl,
  onClose,
}) => {
  const { alert } = useAlert();

  const handlePlayerError = useCallback(
    (err: any) => {
      console.error(err);
      alert.error(`Couldn't play the video: ${videoUrl}`);
      onClose();
    },
    // Can't include alert as part of the dependency, otherwise it will keep triggering component re-renders.
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [videoUrl],
  );

  return (
    <>
      {!!videoUrl && (
        <Dialog open={!!videoUrl} onClose={onClose} fullWidth maxWidth="xl">
          <DialogTitle>
            {title}
            <IconButton
              aria-label="close"
              onClick={onClose}
              sx={{ position: "absolute", right: 8, top: 8, color: (theme) => theme.palette.grey[500] }}
            >
              <CloseIcon />
            </IconButton>
          </DialogTitle>
          <DialogContent>
            <ShakaPlayer autoPlay src={videoUrl} onError={handlePlayerError} />
          </DialogContent>
        </Dialog>
      )}
    </>
  );
};

export { ShakaPlayer, VideoPlayerModal };
